import { memo, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Share2, Lock, Play, Plus, Crown } from "lucide-react";
import { api, mediaSrc, fmtCount, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import SubscribeButton from "@/components/SubscribeButton";
import VideoProgress from "@/components/VideoProgress";

export function LockedMedia({ post }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden" data-testid="locked-overlay">
      {post.creator.avatar_url && (
        <img src={mediaSrc(post.creator.avatar_url)} alt="" className="absolute inset-0 h-full w-full scale-125 object-cover blur-2xl" />
      )}
      <div className="absolute inset-0 bg-[#090A0C]/75" />
      <div className="relative z-10 flex flex-col items-center gap-4 px-8 text-center">
        <div className="rounded-sm border border-[#FF2E4D]/40 bg-[#FF2E4D]/10 p-4">
          <Lock className="h-8 w-8 text-[#FF2E4D]" />
        </div>
        <p className="text-base font-semibold tracking-tight">Subscribers only</p>
        <p className="max-w-xs text-sm text-gray-400">Unlock this and everything else from @{post.creator.username}.</p>
        <SubscribeButton creator={post.creator} testId="locked-subscribe-btn" />
      </div>
    </div>
  );
}

function Rail({ post, onLike, onComments }) {
  const share = async () => {
    const url = `${window.location.origin}/u/${post.creator.username}`;
    try { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); } catch { toast(url); }
  };
  const btn = "group flex flex-col items-center gap-1 text-white";
  const icon = "h-7 w-7 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] transition-transform duration-150 group-hover:scale-110 group-active:scale-95";
  const count = "text-[11px] font-medium tabular-nums tracking-tight text-white/90 drop-shadow";
  return (
    <div className="absolute bottom-5 right-2 z-20 flex flex-col items-center gap-5 md:-right-16 md:bottom-4">
      <Link to={`/u/${post.creator.username}`} className="relative mb-1" data-testid="video-creator-avatar-link">
        <Avatar user={post.creator} size="h-11 w-11" className="ring-[1.5px] ring-white" />
        {post.creator.is_creator && !post.creator.is_subscribed && (
          <span className="absolute -bottom-1.5 left-1/2 flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-sm bg-[#FF2E4D]" data-testid="video-subscribe-btn">
            <Plus className="h-3 w-3" strokeWidth={3} />
          </span>
        )}
      </Link>
      <button onClick={onLike} className={btn} data-testid="video-like-btn" aria-label="Like">
        <Heart strokeWidth={1.75} className={`${icon} ${post.liked ? "heart-pop fill-[#FF2E4D] text-[#FF2E4D]" : ""}`} />
        <span className={count} data-testid="video-like-count">{fmtCount(post.likes_count)}</span>
      </button>
      <button onClick={onComments} className={btn} data-testid="video-comment-btn" aria-label="Comments">
        <MessageCircle strokeWidth={1.75} className={icon} />
        <span className={count}>{fmtCount(post.comments_count)}</span>
      </button>
      <button onClick={share} className={btn} data-testid="video-share-btn" aria-label="Share">
        <Share2 strokeWidth={1.75} className={icon} />
        <span className={count}>Share</span>
      </button>
    </div>
  );
}

export function useLike(initial) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [post, setPost] = useState(initial);
  useEffect(() => setPost(initial), [initial]);
  const like = async () => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPost((p) => ({ ...p, liked: data.liked, likes_count: data.likes_count }));
    } catch (e) { toast.error(formatErr(e)); }
  };
  return [post, setPost, like];
}

let audioAllowed = false;
let unlockedAt = 0;

export function unlockFeedAudio() {
  if (!audioAllowed) unlockedAt = Date.now();
  audioAllowed = true;
  document.querySelectorAll('video[data-feed-active="true"]').forEach((v) => {
    v.muted = false;
    v.play().catch(() => {});
  });
}

function playWithSound(v) {
  v.muted = false;
  v.play().catch(() => {
    if (audioAllowed) return;
    v.muted = true;
    v.play().catch(() => {});
  });
}

function FeedCard({ post: initial, active, near, onComments }) {
  const [post, , like] = useLike(initial);
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);
  const isVideo = !post.locked && post.media_type === "video";

  useEffect(() => {
    if (!active) setPaused(false);
    const v = ref.current;
    if (!v) return;
    if (active) playWithSound(v);
    else { v.pause(); v.currentTime = 0; }
  }, [active, near]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return false;
    if (v.muted || Date.now() - unlockedAt < 800) { unlockFeedAudio(); return false; }
    if (v.paused) v.play().catch(() => {});
    else v.pause();
    return true;
  };

  const [bursts, setBursts] = useState([]);
  const lastTap = useRef({ at: 0, toggled: false });

  const onTap = (e) => {
    const now = Date.now();
    if (now - lastTap.current.at < 300) {
      if (lastTap.current.toggled) toggle();
      lastTap.current = { at: 0, toggled: false };
      const r = e.currentTarget.getBoundingClientRect();
      const b = { id: now, x: e.clientX - r.left, y: e.clientY - r.top, rot: Math.round(Math.random() * 30 - 15) };
      setBursts((bs) => [...bs, b]);
      setTimeout(() => setBursts((bs) => bs.filter((x) => x.id !== b.id)), 900);
      if (!post.liked) like();
      return;
    }
    lastTap.current = { at: now, toggled: toggle() };
  };

  return (
    <section className="relative flex h-full w-full items-center justify-center [contain:layout_paint]" data-testid="feed-card">
      <div className="relative h-full w-full md:h-[calc(100%-2rem)] md:w-auto md:aspect-[9/16]">
      <div className="absolute inset-0 overflow-hidden bg-[#121418] md:rounded-md md:border md:border-[#2A2E37]">
        {post.locked ? (
          <LockedMedia post={post} />
        ) : isVideo && near ? (
          <video ref={ref} src={mediaSrc(post.media_src)} poster={mediaSrc(post.cover_src) || undefined} className="h-full w-full object-cover"
            loop playsInline preload="auto" data-feed-active={active ? "true" : "false"}
            onPlay={() => setPaused(false)} onPause={() => active && setPaused(true)} data-testid="feed-video" />
        ) : isVideo ? (
          post.cover_src ? <img src={mediaSrc(post.cover_src)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" /> : null
        ) : (
          <img src={mediaSrc(post.media_src)} alt={post.caption} loading="lazy" decoding="async" className="h-full w-full object-cover" data-testid="feed-image" />
        )}
        {!post.locked && (
          <div onClick={onTap} className="absolute inset-0 z-[5] touch-manipulation select-none" data-testid="feed-tap-layer">
            {bursts.map((b) => (
              <Heart key={b.id} className="heart-burst pointer-events-none absolute h-24 w-24 fill-[#FF2E4D] text-[#FF2E4D] drop-shadow-[0_4px_16px_rgba(255,46,77,0.5)]"
                style={{ left: b.x - 48, top: b.y - 48, "--rot": `${b.rot}deg` }} strokeWidth={0} data-testid="heart-burst" />
            ))}
          </div>
        )}
        {isVideo && paused && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" data-testid="video-paused-indicator">
            <Play className="h-14 w-14 fill-white/80 text-white/80 drop-shadow-lg" strokeWidth={1} />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-5 left-4 right-16 z-10 space-y-1.5 md:bottom-4">
          <div className="flex items-center gap-2">
            <Link to={`/u/${post.creator.username}`} className="text-sm font-semibold tracking-tight hover:underline" data-testid="video-creator-name">
              {post.creator.name}
            </Link>
            <span className="text-xs text-white/60">@{post.creator.username}</span>
            {post.is_premium && (
              <span className="inline-flex items-center gap-1 rounded-sm bg-[#FF2E4D]/15 px-1.5 py-0.5 font-mono text-[9px] font-medium uppercase tracking-wider text-[#FF2E4D]">
                <Crown className="h-2.5 w-2.5" /> Subs
              </span>
            )}
          </div>
          <p className="line-clamp-2 text-[13px] leading-snug text-white/85" data-testid="video-caption">{post.caption}</p>
          {!post.locked && <SubscribeButton creator={post.creator} testId="feed-subscribe-btn" compact className="!mt-2.5 !px-3 !py-1.5 !text-xs" />}
        </div>
        {isVideo && near && <VideoProgress videoRef={ref} active={active} />}
      </div>
      <Rail post={post} onLike={like} onComments={() => onComments(post)} />
      </div>
    </section>
  );
}

export default memo(FeedCard);
